import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ComponentType } from 'react';
import { cn } from '@/lib/utils';
import { useSettings } from '@/hooks/useProducts';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import { useSiteSettingMutation } from '@/hooks/useSiteSettingMutation';
import { SITE_SHORT_NAME } from '@/config/site';

interface BrandLogoProps {
  /** Lucide mark shown when the tenant has no logo image. */
  Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
  onClick?: () => void;
  /** `default` = the full-size wordmark; `compact` = a one-row header. */
  size?: 'default' | 'compact';
  className?: string;
}

/** The tenant's name as a setting, falling back to the build-time short name. */
export function useStoreName(): string {
  const { data: settingsData } = useSettings();
  return settingsData?.settings?.store_name || SITE_SHORT_NAME;
}

/**
 * The logo link every header variant renders: the AI-generated (or
 * admin-set) brand logo with its dark-mode sibling, falling back to the
 * Lucide mark + wordmark (quick-editable) if unset or if the image fails.
 */
export default function BrandLogo({ Icon, onClick, size = 'default', className }: BrandLogoProps) {
  const { data: settingsData } = useSettings();
  const { isActive: quickEditActive } = useQuickEdit();
  const settingMutation = useSiteSettingMutation();
  const storeName = useStoreName();
  // Falls back to the Lucide mark if unset or if the image fails to load.
  // Public setting: store_logo_url.
  const [logoOk, setLogoOk] = useState(true);
  const logoUrl = logoOk ? ((settingsData?.settings?.store_logo_url as string | undefined) || undefined) : undefined;
  // Dark-mode variant (white colorway, written by Branding Studio apply).
  // Rendered as a CSS-swapped sibling so no JS theme state is needed; absent
  // -> the light logo serves both modes, exactly as before.
  const logoDarkUrl = logoOk
    ? ((settingsData?.settings?.store_logo_dark_url as string | undefined) || undefined)
    : undefined;

  const imgClass =
    size === 'compact'
      ? 'relative z-50 h-9 sm:h-10 w-auto max-w-[55vw] sm:max-w-xs object-contain object-left'
      : 'relative z-50 h-11 sm:h-14 lg:h-16 w-auto max-w-[55vw] sm:max-w-sm lg:max-w-md object-contain object-left';
  const wordmarkClass =
    size === 'compact'
      ? 'font-display text-lg sm:text-xl font-bold text-sage-900 truncate'
      : 'font-display text-xl sm:text-2xl lg:text-3xl font-bold text-sage-900 truncate';

  return (
    <Link
      to="/"
      className={cn(
        'inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded min-w-0',
        className,
      )}
      onClick={onClick}
    >
      {logoUrl ? (
        // Wordmark logo: replaces the title, rendered big and allowed to
        // overflow the header band (the header has no overflow-clip).
        <>
          <img
            src={logoUrl}
            alt={storeName}
            onError={() => setLogoOk(false)}
            className={cn(imgClass, logoDarkUrl && 'dark:hidden')}
          />
          {logoDarkUrl && (
            <img src={logoDarkUrl} alt={storeName} className={cn(imgClass, 'hidden dark:block')} />
          )}
        </>
      ) : (
        <>
          <Icon className={cn('shrink-0 text-sage-600', size === 'compact' ? 'h-5 w-5 sm:h-6 sm:w-6' : 'h-6 w-6 sm:h-7 sm:w-7')} aria-hidden="true" />
          <span className={wordmarkClass}>
            {quickEditActive ? (
              <QuickEditInline
                value={storeName}
                onSave={async (val) => {
                  await settingMutation.mutateAsync({ store_name: val });
                }}
                label="Store name"
                as="span"
                className={wordmarkClass}
              />
            ) : storeName}
          </span>
        </>
      )}
    </Link>
  );
}
